import { useRouter } from 'vue-router'
import { useCustomerStore } from '@/stores/customer'
import { storeToRefs } from 'pinia'
import type { Contract, InsuranceCase, Message } from '@/types/customer'
import { ContractType, MessageBindingType } from '@/types/customer'
import type { RouteLocationRaw } from 'vue-router'

export function useResourceNavigation() {
  const router = useRouter()
  const customerStore = useCustomerStore()
  const { selectedClientId } = storeToRefs(customerStore)
  const homeRoute = {
    name: 'home',
    params: {
      clientId: selectedClientId.value?.toString(),
    },
  }

  function getContractRoute(contract: { type: ContractType; id: number }): RouteLocationRaw {
    const clientId = selectedClientId.value?.toString()
    if (!clientId) return homeRoute

    if (contract.type === ContractType.subcontract) {
      return {
        name: 'subcontract-detail',
        params: {
          clientId,
          subcontractId: contract.id.toString(),
        },
      }
    } else {
      return {
        name: 'contract-detail',
        params: {
          clientId,
          contractId: contract.id.toString(),
        },
      }
    }
  }

  function getInsuranceCaseRoute(insuranceCase: { id: number }): RouteLocationRaw {
    const clientId = selectedClientId.value?.toString()
    if (!clientId) return homeRoute

    return {
      name: 'insurance-case-detail',
      params: {
        clientId,
        caseId: insuranceCase.id.toString(),
      },
    }
  }

  function getMessageRoute(message: Message): RouteLocationRaw | null {
    if (!message.bindingType || !message.bindingId) {
      return null
    }

    const clientId = selectedClientId.value?.toString()
    if (!clientId) return null

    switch (message.bindingType) {
      case MessageBindingType.contract:
        return {
          name: 'contract-detail',
          params: {
            clientId,
            contractId: message.bindingId.toString(),
          },
        }
      case MessageBindingType.subcontract:
        return {
          name: 'subcontract-detail',
          params: {
            clientId,
            subcontractId: message.bindingId.toString(),
          },
        }
      case MessageBindingType.insuranceCase:
        return {
          name: 'insurance-case-detail',
          params: {
            clientId,
            caseId: message.bindingId.toString(),
          },
        }
      default:
        return homeRoute
    }
  }

  function getHomeRoute(): RouteLocationRaw {
    return homeRoute
  }

  function navigateToContract(contract: Contract) {
    const route = getContractRoute(contract)
    if (route) {
      router.push(route)
    }
  }

  function navigateToInsuranceCase(insuranceCase: InsuranceCase) {
    const route = getInsuranceCaseRoute(insuranceCase)
    if (route) {
      router.push(route)
    }
  }

  function navigateToMessage(message: Message) {
    const route = getMessageRoute(message)
    if (route) {
      router.push(route)
    } else {
      console.warn('Message missing binding information')
    }
  }

  return {
    navigateToContract,
    navigateToInsuranceCase,
    navigateToMessage,
    getContractRoute,
    getInsuranceCaseRoute,
    getMessageRoute,
    getHomeRoute,
  }
}

